import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CrudActions } from "../components/Crud";
import DatePicker from "../components/DatePicker";
import BreakdownChip from "../components/BreakdownChip";
import ProjectBreakdown from "../components/ProjectBreakdown";
import BackButton from "../components/BackButton";
import { ArrowLeft, Plus, BookOpen, ChevronDown, ChevronRight } from "lucide-react";

const TABS = ["lecturer", "assessments", "projects", "reading list"];

export default function ClassDetail() {
    const { id } = useParams();
    const [cls, setCls] = useState(null);
    const [tab, setTab] = useState("lecturer");
    useEffect(() => { api.get("/classes").then(r => setCls(r.data.find(c => c.id === id))); }, [id]);
    if (!cls) return <div className="p-6">Loading…</div>;
    return (
        <div data-testid="class-detail-page">
            <BackButton to="/classes" label="All classes" />
            <div className="font-script text-[var(--brand-orange)] text-xl">{cls.code}</div>
            <h1 className="font-serif text-4xl mb-5">{cls.name}</h1>
            <ClassSummary classId={id} />
            <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-thin">
                {TABS.map(t => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`class-tab-${t.replace(/\s/g, "-")}`}
                        className={`px-4 py-1.5 rounded-full text-sm capitalize ${tab === t ? "brand-gradient text-white" : "btn-ghost"}`}>{t}</button>
                ))}
            </div>
            {tab === "lecturer" && (
                <div className="space-y-5" data-testid="class-tab-lecturer-content">
                    <div className="card-warm p-6 grid sm:grid-cols-2 gap-4">
                        {[["Name", cls.lecturer_name], ["Email", cls.lecturer_email], ["Phone", cls.lecturer_phone],
                          ["Office", cls.lecturer_office], ["Schedule", cls.schedule], ["Semester", cls.semester]].map(([l, v]) => (
                            <div key={l}>
                                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">{l}</div>
                                <div className="text-sm">{v || "—"}</div>
                            </div>
                        ))}
                    </div>
                    <ScheduleSlots classId={id} className={cls.name} />
                </div>
            )}
            {tab === "assessments" && <Assessments classId={id} />}
            {tab === "projects" && <ClassProjects classId={id} className={cls.name} />}
            {tab === "reading list" && <ReadingList classId={id} />}
        </div>
    );
}

function useScoped(name, key, val) {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get(`/${name}`).then(r => setItems(r.data.filter(i => i[key] === val))); }, [name, val]); // eslint-disable-line
    const create = async (b) => { const { data } = await api.post(`/${name}`, { ...b, [key]: val }); setItems(x => [data, ...x]); };
    const update = async (id, b) => { const { data } = await api.put(`/${name}/${id}`, b); setItems(x => x.map(i => i.id === id ? data : i)); };
    const remove = async (id) => { await api.delete(`/${name}/${id}`); setItems(x => x.filter(i => i.id !== id)); };
    return { items, create, update, remove };
}

function Assessments({ classId }) {
    const { items, create, update, remove } = useScoped("assessments", "class_id", classId);
    const [form, setForm] = useState({ title: "", type: "assignment", due: "", weight: "", expected_grade: "", received_grade: "" });
    return (
        <div data-testid="class-tab-assessments-content">
            <form onSubmit={(e) => { e.preventDefault(); create(form); setForm({ title: "", type: "assignment", due: "", weight: "", expected_grade: "", received_grade: "" }); }}
                  className="card-warm p-4 grid sm:grid-cols-6 gap-2 mb-3" data-testid="assessment-form">
                <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] sm:col-span-2" />
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                    <option value="assignment">Assignment</option><option value="exam">Exam</option><option value="essay">Essay</option><option value="lab">Lab</option>
                </select>
                <DatePicker value={form.due} onChange={(v) => setForm({ ...form, due: v })}
                            testid="cls-asg-due" placeholder="Due date" />
                <input placeholder="Weight %" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary"><Plus size={14} className="inline" /> Add</button>
            </form>
            <div className="space-y-2">
                {items.map(a => (
                    <AssessmentRow key={a.id} a={a} update={update} remove={remove} />
                ))}
            </div>
        </div>
    );
}

function AssessmentRow({ a, update, remove }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="card-warm" data-testid={`assessment-row-${a.id}`}>
            <div className="p-3 flex flex-wrap items-center gap-3">
                <button onClick={() => setOpen(!open)}
                        data-testid={`assessment-expand-${a.id}`}
                        className="text-[var(--text-ink)]/55 hover:text-[var(--brand-orange)]"
                        title={open ? "Collapse breakdown" : "Expand breakdown"}>
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] capitalize">{a.type}</span>
                <div className="flex-1 min-w-[10rem]">
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-[var(--text-ink)]/60">Due {a.due || "—"} · {a.weight || "?"}%</div>
                </div>
                <label className="text-xs text-[var(--text-ink)]/65 flex items-center gap-1">
                    Expected
                    <input placeholder="—" defaultValue={a.expected_grade} onBlur={(e) => e.target.value !== (a.expected_grade || "") && update(a.id, { expected_grade: e.target.value })}
                           data-testid={`assessment-expected-${a.id}`}
                           className="w-20 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
                </label>
                <label className="text-xs text-[var(--text-ink)]/65 flex items-center gap-1">
                    Received
                    <input placeholder="—" defaultValue={a.received_grade} onBlur={(e) => e.target.value !== (a.received_grade || "") && update(a.id, { received_grade: e.target.value })}
                           data-testid={`assessment-received-${a.id}`}
                           className="w-20 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
                </label>
                <CrudActions idBase={`ass-${a.id}`}
                    onArchive={() => update(a.id, { archived: !a.archived })}
                    onDelete={() => window.confirm(`Delete "${a.title}"?`) && remove(a.id)} archived={a.archived} />
            </div>
            <div className="px-3 pb-3" data-testid={`assessment-chip-wrap-${a.id}`}>
                <BreakdownChip scope="assessment" id={a.id} testidSuffix={`ass-${a.id}`} />
            </div>
            {open && (
                <div className="px-3 pb-4 pt-1 border-t border-[rgba(26,19,24,0.06)]" data-testid={`assessment-breakdown-${a.id}`}>
                    <ProjectBreakdown scope="assessment" scopeId={a.id} />
                </div>
            )}
        </div>
    );
}

function ClassProjects({ classId, className }) {
    const { items, create, update, remove } = useScoped("projects", "class_id", classId);
    const [title, setTitle] = useState("");
    return (
        <div data-testid="class-tab-projects-content">
            <form onSubmit={(e) => { e.preventDefault(); if (title) { create({ title, class_name: className }); setTitle(""); } }}
                  className="card-warm p-4 flex gap-2 mb-3">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Project title" className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary"><Plus size={14} className="inline" /> Add</button>
            </form>
            <div className="space-y-2">
                {items.map(p => (
                    <ClassProjectRow key={p.id} p={p} update={update} remove={remove} />
                ))}
            </div>
        </div>
    );
}

function ReadingList({ classId }) {
    const { items, create, update, remove } = useScoped("reading_list", "class_id", classId);
    const [title, setTitle] = useState("");
    return (
        <div data-testid="class-tab-reading-list-content">
            <form onSubmit={(e) => { e.preventDefault(); if (title) { create({ title, done: false }); setTitle(""); } }}
                  className="card-warm p-4 flex gap-2 mb-3">
                <BookOpen size={18} className="self-center text-[var(--brand-purple)]" />
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a book, article, or chapter…" className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary">Add</button>
            </form>
            <div className="space-y-1">
                {items.map(r => (
                    <div key={r.id} className="card-warm p-3 flex items-center gap-3">
                        <input type="checkbox" checked={!!r.done} onChange={(e) => update(r.id, { done: e.target.checked })} data-testid={`reading-${r.id}-check`} />
                        <div className={`flex-1 ${r.done ? "line-through text-[var(--text-ink)]/40" : ""}`}>{r.title}</div>
                        <CrudActions idBase={`rd-${r.id}`} onDelete={() => remove(r.id)} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function ScheduleSlots({ classId, className }) {
    const { items, create, remove } = useScoped("events", "class_id", classId);
    const [form, setForm] = useState({ day: "Monday", start_time: "09:00", end_time: "10:00", location: "" });
    const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    // Show unique day/time combos (since we create 12 weekly occurrences per slot)
    const uniqueSlots = Object.values(items.filter(i => i.day).reduce((acc, s) => {
        const k = `${s.day}|${s.start_time}|${s.end_time}|${s.location || ""}`;
        if (!acc[k]) acc[k] = s;
        return acc;
    }, {}));

    const removeSlot = async (slot) => {
        if (!window.confirm(`Remove all "${slot.day} ${slot.start_time}" occurrences?`)) return;
        const matches = items.filter(i => i.day === slot.day && i.start_time === slot.start_time && i.end_time === slot.end_time && (i.location || "") === (slot.location || ""));
        for (const m of matches) await remove(m.id);
    };
    const add = async (e) => {
        e.preventDefault();
        // Create 12 weekly occurrences so the slot recurs on the calendar
        const target = DAYS.indexOf(form.day);
        const now = new Date();
        const offset = (target - ((now.getDay() + 6) % 7) + 7) % 7;
        const [sh, sm] = form.start_time.split(":").map(Number);
        const [eh, em] = form.end_time.split(":").map(Number);
        for (let w = 0; w < 12; w++) {
            const base = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset + w * 7);
            const start = new Date(base); start.setHours(sh, sm, 0, 0);
            const end = new Date(base); end.setHours(eh, em, 0, 0);
            await create({
                title: `${className} (${form.day})`,
                start: start.toISOString().slice(0, 16),
                end: end.toISOString().slice(0, 16),
                location: form.location,
                day: form.day, start_time: form.start_time, end_time: form.end_time,
                kind: "class",
                link_to: `/classes/${classId}`,
            });
        }
        setForm({ ...form, location: "" });
    };

    return (
        <div className="card-warm p-5" data-testid="schedule-slots">
            <div className="font-serif text-2xl mb-3">Class schedule</div>
            <p className="text-xs text-[var(--text-ink)]/60 mb-3">Add weekly time slots — they appear on your Calendar and Daily To-Do automatically.</p>
            <form onSubmit={add} className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="schedule-form">
                <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="schedule-day" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                    {DAYS.map(d => <option key={d}>{d}</option>)}
                </select>
                <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="schedule-start" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="schedule-end" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <input placeholder="Room / link" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary" data-testid="schedule-add-btn"><Plus size={14} className="inline" /> Add slot</button>
            </form>
            <div className="space-y-1">
                {uniqueSlots.map(s => (
                    <div key={s.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-[rgba(26,19,24,0.06)] last:border-0">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]">{s.day}</span>
                        <span className="font-mono">{s.start_time} – {s.end_time}</span>
                        <span className="flex-1">{s.location || ""}</span>
                        <span className="text-[10px] text-[var(--text-ink)]/45">weekly · 12 wks</span>
                        <CrudActions idBase={`slot-${s.id}`} onDelete={() => removeSlot(s)} />
                    </div>
                ))}
                {uniqueSlots.length === 0 && <div className="text-sm text-[var(--text-ink)]/55">No slots yet.</div>}
            </div>
        </div>
    );
}


function ClassProjectRow({ p, update, remove }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="card-warm p-3" data-testid={`class-project-row-${p.id}`}>
            <div className="flex items-center gap-3">
                <button onClick={() => setOpen(!open)} data-testid={`class-project-expand-${p.id}`}
                        className="text-[var(--text-ink)]/55 hover:text-[var(--brand-orange)]"
                        title={open ? "Hide breakdown" : "Show breakdown"}>
                    {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <Link to={`/projects/${p.id}`} className="flex-1 truncate hover:text-[var(--brand-purple)] transition" data-testid={`open-project-${p.id}`}>{p.title}</Link>
                <CrudActions idBase={`cp-${p.id}`} onArchive={() => update(p.id, { archived: !p.archived })} onDelete={() => remove(p.id)} archived={p.archived} />
            </div>
            <div className="mt-2"><BreakdownChip scope="project" id={p.id} testidSuffix={`cprj-${p.id}`} /></div>
            {open && (
                <div className="mt-3 border-t border-[rgba(26,19,24,0.06)] pt-3" data-testid={`class-project-breakdown-${p.id}`}>
                    <ProjectBreakdown scope="project" scopeId={p.id} />
                </div>
            )}
        </div>
    );
}


function ClassSummary({ classId }) {
    const [stats, setStats] = useState({ upcoming: 0, avgGrade: null, gradedCount: 0, totalAssess: 0, readingPct: 0, readingDone: 0, readingTotal: 0 });
    useEffect(() => {
        (async () => {
            const [as, rd] = await Promise.all([api.get("/assessments"), api.get("/reading_list")]);
            const mine = as.data.filter(a => a.class_id === classId);
            const today = new Date().toISOString().slice(0, 10);
            const upcoming = mine.filter(a => !a.archived && (!a.due || a.due >= today) && !a.received_grade).length;
            const graded = mine.map(a => parseFloat(a.received_grade)).filter(v => !isNaN(v));
            const avgGrade = graded.length ? Math.round(graded.reduce((s, v) => s + v, 0) / graded.length) : null;
            const reads = rd.data.filter(r => r.class_id === classId);
            const done = reads.filter(r => r.done).length;
            const pct = reads.length ? Math.round((done / reads.length) * 100) : 0;
            setStats({ upcoming, avgGrade, gradedCount: graded.length, totalAssess: mine.length, readingPct: pct, readingDone: done, readingTotal: reads.length });
        })().catch(() => {});
    }, [classId]);
    const cardCls = "card-warm p-4";
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 fade-up" data-testid="class-summary">
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Upcoming</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-purple)" }}>{stats.upcoming}</div>
                <div className="text-xs text-[var(--text-ink)]/55">assessment{stats.upcoming === 1 ? "" : "s"}</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Avg grade</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-magenta)" }}>
                    {stats.avgGrade !== null ? `${stats.avgGrade}%` : "—"}
                </div>
                <div className="text-xs text-[var(--text-ink)]/55">{stats.gradedCount}/{stats.totalAssess} graded</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Reading list</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--brand-orange)" }}>{stats.readingPct}%</div>
                <div className="h-1.5 mt-2 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                    <div className="h-full brand-gradient" style={{ width: `${stats.readingPct}%` }} />
                </div>
                <div className="text-xs text-[var(--text-ink)]/55 mt-1">{stats.readingDone}/{stats.readingTotal} read</div>
            </div>
            <div className={cardCls}>
                <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">Total assessments</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "var(--status-green)" }}>{stats.totalAssess}</div>
                <div className="text-xs text-[var(--text-ink)]/55">tracked this class</div>
            </div>
        </div>
    );
}

